import { ErrorMessage } from '@/components/common/ErrorMessage';
import { ConfirmDeleteDialog } from '@/components/dialogs/ConfirmDeleteDialog';
import { CreateProjectCard } from '@/components/projects/CreateProjectCard';
import { FilterControls } from '@/components/projects/FilterControls';
import { ProjectGrid } from '@/components/projects/ProjectGrid';
import { ProjectsTable } from '@/components/projects/ProjectsTable';
import { SortControls } from '@/components/projects/SortControls';
import { UploadQueuePanel } from '@/components/projects/UploadQueuePanel';
import { useUploadQueueContext } from '@/context/UploadQueueContext';
import { useProjects } from '@/hooks/useProjects';
import type { SortConfig } from '@/types/project.types';
import { ChevronLeft, ChevronRight, LayoutGrid, Settings, Table2, Trash2 } from 'lucide-react';
import { Button } from 'primereact/button';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

type ViewMode = 'grid' | 'table';

export const Home: FC = () => {
  const { t } = useTranslation();
  const {
    projects,
    isLoading,
    error,
    bulkDeleteProjects,
    refetch,
    page,
    pageSize,
    setPage,
    hasNextPage,
    hasPrevPage,
    totalPages,
    totalCount,
    sort,
    order,
    setSort,
    ownerFilter,
    setOwnerFilter,
  } = useProjects();
  const { uploads, startUpload, cancelUpload } = useUploadQueueContext();
  const navigate = useNavigate();

  // Server-side sort exposed to the sort/table controls as a SortConfig.
  const sortConfig: SortConfig = { field: sort, order };
  const handleSortChange = (config: SortConfig) => setSort(config.field, config.order);

  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeletingProjects, setIsDeletingProjects] = useState(false);

  const handleProjectClick = (projectId: string) => {
    navigate(`/project/${projectId}`);
  };

  const handleUserClick = (ownerId: string) => {
    // If the clicked user is already filtered, switch back to "All users".
    setOwnerFilter(ownerFilter === ownerId ? null : ownerId);
  };

  const handleProjectSelect = (projectId: string, selected: boolean) => {
    setSelectedProjectIds((prev) => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(projectId);
      } else {
        newSet.delete(projectId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedProjectIds(new Set(projects.map((p) => p.id)));
    } else {
      setSelectedProjectIds(new Set());
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedProjectIds.size === 0) return;

    setIsDeletingProjects(true);
    try {
      await bulkDeleteProjects(Array.from(selectedProjectIds));
      setSelectedProjectIds(new Set());
      setShowDeleteDialog(false);
    } catch (_err) {
      // Error handling is done in bulkDeleteProjects
    } finally {
      setIsDeletingProjects(false);
    }
  };

  return (
    <div className="max-w-[calc(80rem+4cm)] mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h1 className="text-3xl font-bold text-gray-900">{t('home.title')}</h1>
            {/* View mode toggle */}
            <div className="flex items-center gap-2 border border-gray-300 rounded-lg p-1">
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded transition-colors ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                title={t('home.gridView')}
              >
                <LayoutGrid size={18} />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`p-2 rounded transition-colors ${viewMode === 'table' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                title={t('home.tableView')}
              >
                <Table2 size={18} />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Delete selected button */}
            {selectedProjectIds.size > 0 && (
              <button
                type="button"
                onClick={() => setShowDeleteDialog(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 size={18} />
                <span>
                  {t('home.deleteSelected')} ({selectedProjectIds.size})
                </span>
              </button>
            )}

            {/* Sort and Filter controls - only show in grid view */}
            {viewMode === 'grid' && (
              <>
                <SortControls sortConfig={sortConfig} onSortChange={handleSortChange} />
                <FilterControls
                  projects={projects}
                  selectedOwnerId={ownerFilter}
                  onOwnerFilterChange={setOwnerFilter}
                />
              </>
            )}
            <button
              type="button"
              onClick={() => navigate('/tools')}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
              title={t('toolManagement.title')}
            >
              <Settings size={18} />
              {t('toolManagement.title')}
            </button>
          </div>
        </div>

        {/* Error message */}
        {error && <ErrorMessage message={error} />}

        {/* Project view */}
        {viewMode === 'grid' ? (
          <ProjectGrid
            projects={projects}
            isLoading={isLoading}
            onProjectClick={handleProjectClick}
            onUserClick={handleUserClick}
            selectedProjectIds={selectedProjectIds}
            onProjectSelect={handleProjectSelect}
            onStartUpload={(file) => startUpload(file, refetch)}
          />
        ) : (
          <div className="flex flex-col gap-6">
            <CreateProjectCard compact={true} onStartUpload={(file) => startUpload(file, refetch)} />
            <ProjectsTable
              projects={projects}
              selectedProjectIds={selectedProjectIds}
              onProjectClick={handleProjectClick}
              onProjectSelect={handleProjectSelect}
              onSelectAll={handleSelectAll}
              onUserClick={handleUserClick}
              sortConfig={sortConfig}
              onSortChange={handleSortChange}
            />
          </div>
        )}

        {/* Pagination bar */}
        {!isLoading && (hasPrevPage || hasNextPage) && (
          <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50/60 px-4 py-2 rounded-lg">
            <div className="flex items-center justify-center gap-2 flex-1">
              <Button
                icon={<ChevronLeft size={18} />}
                className="p-button-text p-button-sm"
                disabled={!hasPrevPage}
                onClick={() => setPage(Math.max(1, page - 1))}
                aria-label={t('common.previousPage')}
              />
              <span className="text-sm text-gray-600">
                {totalPages != null
                  ? `${t('common.page')} ${page} ${t('common.of')} ${totalPages}`
                  : `${t('common.page')} ${page}`}
              </span>
              <Button
                icon={<ChevronRight size={18} />}
                className="p-button-text p-button-sm"
                disabled={!hasNextPage}
                onClick={() => setPage(page + 1)}
                aria-label={t('common.nextPage')}
              />
            </div>
            {totalCount != null && (
              <span className="text-sm text-gray-600 shrink-0">{`${Math.min(page * pageSize, totalCount)} / ${totalCount}`}</span>
            )}
          </div>
        )}

        <UploadQueuePanel uploads={uploads} onCancel={cancelUpload} />

        {/* Delete confirmation dialog */}
        <ConfirmDeleteDialog
          visible={showDeleteDialog}
          title={t('home.deleteTitle')}
          message={t('home.deleteConfirm', { count: selectedProjectIds.size })}
          isDeleting={isDeletingProjects}
          onConfirm={handleDeleteSelected}
          onCancel={() => setShowDeleteDialog(false)}
        />
      </div>
    </div>
  );
};
